import { computePeriodReturns } from '../../utils/vectorCapitalScenario';
import {
  computeCoinbaseYouMetrics,
  isCoinbaseBroker,
} from './clientReportingCoinbaseMetrics';
import {
  materialityFloor,
  trimToFunded,
  inceptionDate,
  preparePortfolioSeries,
} from './clientReportingInception';
import { chainReturns, effectiveFlowsByDate } from './clientReportingReturnChain';

export const EMPTY_BENCHMARK_METRICS = {
  gain: null,
  monthly: null,
  yearly: null,
  drawdown: null,
  profit: null,
};

/**
 * @param {object[]} [portfolioSeries] Client points, used to anchor "All".
 */
export function clientReportingRangeToIso(effectiveRange, portfolioSeries = null) {
  if (!effectiveRange || effectiveRange === 'All') {
    // "All" used to send null, so the server returned the vendor's ENTIRE price
    // history — SPY read +554% beside an account a few weeks old, which is not a
    // comparison of anything. Anchor it to the client's inception so both sides
    // cover the same window.
    const inception = inceptionDate(portfolioSeries);
    return {
      startDate: inception || null,
      endDate: null,
    };
  }
  if (typeof effectiveRange === 'object' && effectiveRange.start && effectiveRange.end) {
    return { startDate: effectiveRange.start, endDate: effectiveRange.end };
  }

  const now = new Date();
  const endDate = now.toISOString().slice(0, 10);
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  switch (effectiveRange) {
    case 'YTD':
      return { startDate: `${start.getUTCFullYear()}-01-01`, endDate };
    case '1M':
      start.setUTCMonth(start.getUTCMonth() - 1);
      break;
    case '6M':
      start.setUTCMonth(start.getUTCMonth() - 6);
      break;
    case '1Y':
      start.setUTCFullYear(start.getUTCFullYear() - 1);
      break;
    default:
      return { startDate: null, endDate: null };
  }

  return { startDate: start.toISOString().slice(0, 10), endDate };
}

function applyPortfolioMetrics(rawSeries) {
  if (!rawSeries?.length) {
    return { totalReturn: 0, maxDrawdown: 0 };
  }

  // Same chain as the Growth and Drawdown tabs, so the tile and the chart cannot
  // disagree on identical data.
  const { totalReturnPct, maxDrawdownPct } = chainReturns(rawSeries);
  return { totalReturn: totalReturnPct, maxDrawdown: maxDrawdownPct };
}

/**
 * Last line of defence: a Gain that contradicts the account's own equity.
 *
 * This used to catch blow-ups only (|return| > 9999.99%), which left the
 * opposite failure — a chain collapsed to ~0 by a junk NAV row — to print
 * "-100.00%" next to a healthy five-figure balance in production.
 *
 * A -100% return asserts the capital is gone, and that is checkable: if the
 * account still holds a material balance on its last day, the claim is false
 * whatever the chain says. A genuine wipeout (final equity below the floor) is
 * left alone, because then -100% is the truth.
 */
function sanitizeGainPct(totalReturn, profit, points, netContributed = null) {
  if (!Number.isFinite(totalReturn)) return null;

  const prepared = preparePortfolioSeries(points);
  const funded = prepared.length ? prepared : trimToFunded(points);
  const finalValue = Number(funded[funded.length - 1]?.value) || 0;
  const stillFunded = finalValue >= materialityFloor(funded);
  const contradictsBalance = totalReturn <= -95 && stillFunded;

  const basisFromInvestment = Number(netContributed) > 0 ? Number(netContributed) : null;
  const simpleFromInvestment = (
    basisFromInvestment && Number.isFinite(profit)
      ? (profit / basisFromInvestment) * 100
      : null
  );

  // Deposits mis-counted as return: profit negative on invested capital while
  // gain reads strongly positive (e.g. +136% gain, -$6k profit on $130k in).
  if (
    Number.isFinite(simpleFromInvestment)
    && profit < 0
    && totalReturn > 5
  ) {
    return simpleFromInvestment;
  }
  if (
    Number.isFinite(simpleFromInvestment)
    && Math.abs(totalReturn - simpleFromInvestment) > 15
    && Math.abs(totalReturn) > 30
  ) {
    return simpleFromInvestment;
  }

  if (Math.abs(totalReturn) <= 9999.99 && !contradictsBalance) return totalReturn;
  if (!Number.isFinite(profit)) return totalReturn;

  const first = Number(funded[0]?.value) || 0;
  const inflows = funded.reduce(
    (sum, point) => sum + Math.max(Number(point.netFlow) || 0, 0),
    0,
  );
  const basis = basisFromInvestment || Math.max(Math.abs(first), inflows, 1);
  const simple = (profit / basis) * 100;
  return Number.isFinite(simple) ? simple : totalReturn;
}

/**
 * Time-weighted index series for the trailing-period cells.
 *
 * This used to hand `computePeriodReturns` the raw equity, which is a bare
 * ratio of two NAVs — so a deposit inside the last 30 days counted as a gain and
 * "Monthly" disagreed with "Gain" on the same card, even though Gain strips
 * flows. Chaining the same flow-adjusted index everything else uses makes the
 * whole card consistent.
 *
 * The index is rebased to 100 so it reads as a value series to the caller;
 * period returns are ratios, so the base is arbitrary.
 */
function normalizedEquitySeries(rawPoints) {
  return chainReturns(rawPoints).points
    .map((point) => ({ date: point.date, normalized: point.index * 100 }));
}

export function portfolioBaseValue(points) {
  if (!points?.length) return 100000;
  // Alpaca and NinjaTrader often open with $0 or dust rows before the funding
  // deposit. Using that as the rebase anchor sent benchmarks to the $100k
  // fallback while the account chart showed ~$15k–$50k — lines never overlapped.
  const funded = trimToFunded(points);
  const first = Number(funded[0]?.value);
  return Number.isFinite(first) && first > 0 ? first : 100000;
}

export function computeYouMetricsFromSeries(points, { broker, realizedProfit = null, netContributed = null } = {}) {
  if (!points?.length || points.length < 2) return { ...EMPTY_BENCHMARK_METRICS };

  if (isCoinbaseBroker(broker)) {
    return computeCoinbaseYouMetrics(points, { realizedProfit, netContributed });
  }

  const prepared = preparePortfolioSeries(points);
  const series = prepared.length ? prepared : points;
  // chainReturns prepares internally — pass raw points so flows are not relocated twice.
  const { totalReturn, maxDrawdown } = applyPortfolioMetrics(points);
  const first = Number(series[0]?.value) || 0;
  const last = Number(series[series.length - 1]?.value) || 0;
  const flows = series
    .slice(1)
    .reduce((sum, point) => sum + (Number(point.netFlow) || 0), 0);
  const profit = Number.isFinite(last - first - flows) ? last - first - flows : null;
  const period = computePeriodReturns(normalizedEquitySeries(points));

  return {
    gain: sanitizeGainPct(totalReturn, profit, points, netContributed),
    monthly: period.monthPct,
    yearly: period.yearPct,
    drawdown: Number.isFinite(maxDrawdown) ? Math.abs(maxDrawdown) : null,
    profit,
  };
}

export function computeYouSummary(points, metrics = EMPTY_BENCHMARK_METRICS) {
  if (!points?.length) {
    return { equity: null, changeAmount: null, changePct: null };
  }

  const last = Number(points[points.length - 1].value);
  return {
    equity: Number.isFinite(last) ? last : null,
    changeAmount: metrics?.profit ?? null,
    changePct: metrics?.gain ?? null,
  };
}

export function computeBenchmarkMetricsFromApi(data) {
  const series = data?.series || [];
  const stats = data?.stats || {};
  const baseValue = Number(data?.baseValue) > 0 ? Number(data.baseValue) : 100000;
  const period = computePeriodReturns(series);
  const profit = Number.isFinite(stats.endingValue) && Number.isFinite(stats.startingValue)
    ? stats.endingValue - stats.startingValue
    : (Number(stats.totalReturnPct) / 100) * baseValue;

  return {
    gain: Number.isFinite(Number(stats.totalReturnPct)) ? Number(stats.totalReturnPct) : null,
    monthly: period.monthPct,
    yearly: period.yearPct,
    drawdown: Number.isFinite(Number(stats.maxDrawdownPct)) ? Number(stats.maxDrawdownPct) : null,
    profit: Number.isFinite(profit) ? profit : null,
  };
}

function benchmarkValueForTab(point, chartTab, baseValue, peakRef) {
  const normalized = Number(point?.normalized);
  if (!Number.isFinite(normalized) || !Number.isFinite(baseValue) || baseValue <= 0) return null;

  if (chartTab === 'equity') return normalized;
  if (chartTab === 'growth') return ((normalized / baseValue) - 1) * 100;
  if (chartTab === 'profit') return normalized - baseValue;
  if (chartTab === 'drawdown') {
    peakRef.peak = Math.max(peakRef.peak, normalized);
    return peakRef.peak > 0 ? ((normalized - peakRef.peak) / peakRef.peak) * 100 : 0;
  }
  return normalized;
}

function seedBenchmarkPoint(points, targetDate) {
  if (!targetDate || !points?.length) return null;
  let seed = null;
  for (const point of points) {
    if (point.date <= targetDate) seed = point;
    else break;
  }
  return seed;
}

/**
 * Equity-tab overlay: compound benchmark returns on invested capital and step
 * the notional up/down when deposits or withdrawals land (same flow timing as
 * chainReturns). Answers "what if each contribution had gone into SPY instead."
 */
function overlayFlowAdjustedEquityBenchmarks({
  chartData,
  bmEntries,
  pointMaps,
  overlayStartDate,
  baseValue,
  primarySeriesName,
  flowByDate,
}) {
  const stateByName = new Map(
    bmEntries.map(({ name, points }) => {
      const firstOverlayDate = chartData.find(
        (row) => !overlayStartDate || row.date >= overlayStartDate,
      )?.date || chartData[0]?.date;
      const seed = seedBenchmarkPoint(points, firstOverlayDate);
      const seedNorm = Number(seed?.normalized);
      return [name, {
        capital: null,
        lastBmNorm: Number.isFinite(seedNorm) && seedNorm > 0 ? seedNorm : null,
        seeded: false,
      }];
    }),
  );

  return chartData.map((chartPoint) => {
    const date = chartPoint.date;
    const row = { ...chartPoint };
    const beforeOverlay = overlayStartDate && date < overlayStartDate;

    bmEntries.forEach(({ name }) => {
      if (beforeOverlay) {
        row[name] = null;
        return;
      }

      const state = stateByName.get(name);
      const bmPoint = pointMaps.get(name)?.get(date);

      if (!state.seeded) {
        const accountEquity = primarySeriesName != null
          ? Number(chartPoint[primarySeriesName])
          : NaN;
        state.capital = Number.isFinite(accountEquity) && accountEquity > 0
          ? accountEquity
          : baseValue;
        if (bmPoint) {
          state.lastBmNorm = Number(bmPoint.normalized);
        }
        state.seeded = true;
        row[name] = state.capital;
        return;
      }

      if (bmPoint) {
        const newNorm = Number(bmPoint.normalized);
        if (Number.isFinite(newNorm) && state.lastBmNorm > 0) {
          state.capital *= newNorm / state.lastBmNorm;
        }
        if (Number.isFinite(newNorm)) state.lastBmNorm = newNorm;
      }

      const flow = flowByDate.get(date) || 0;
      if (flow !== 0) state.capital += flow;

      row[name] = state.capital;
    });

    return row;
  });
}

/** Overlay selected benchmark curves onto the active chart tab data. */
export function mergeBenchmarkSeriesIntoChartData({
  chartData = [],
  seriesOrder = [],
  benchmarkDataById = {},
  selectedBenchmarkIds = new Set(),
  chartTab = 'equity',
  baseValue = 100000,
  benchmarks = [],
  /** ISO date the account was first funded — benchmarks align here, not on dust rows. */
  overlayStartDate = null,
  /** Raw account points ({ date, value, netFlow }) for deposit-aware equity overlays. */
  portfolioSeries = null,
}) {
  if (!selectedBenchmarkIds?.size || !chartData.length) {
    return { data: chartData, seriesOrder };
  }

  const chartStart = chartData[0]?.date;
  const rangeStart = overlayStartDate && overlayStartDate > chartStart
    ? overlayStartDate
    : chartStart;
  const bmEntries = benchmarks
    .filter((benchmark) => selectedBenchmarkIds.has(benchmark.id))
    .map((benchmark) => {
      const points = (benchmarkDataById[benchmark.id]?.series || [])
        .filter((point) => point?.date >= rangeStart);
      return {
        benchmark,
        name: benchmark.shortLabel,
        points,
      };
    })
    .filter((entry) => entry.points.length);

  if (!bmEntries.length) {
    return { data: chartData, seriesOrder };
  }

  const pointMaps = new Map(
    bmEntries.map(({ name, points }) => [name, new Map(points.map((point) => [point.date, point]))]),
  );

  const useFlowAdjustedEquity = chartTab === 'equity' && portfolioSeries?.length;
  if (useFlowAdjustedEquity) {
    const flowByDate = effectiveFlowsByDate(portfolioSeries);
    const primarySeriesName = seriesOrder[0] || null;
    const data = overlayFlowAdjustedEquityBenchmarks({
      chartData,
      bmEntries,
      pointMaps,
      overlayStartDate,
      baseValue,
      primarySeriesName,
      flowByDate,
    });
    return {
      data,
      seriesOrder: [...seriesOrder, ...bmEntries.map((entry) => entry.name)],
    };
  }

  const chartByDate = new Map(chartData.map((point) => [point.date, { ...point }]));
  const lastValues = new Map(
    bmEntries.map(({ name }) => [name, chartTab === 'equity' ? null : 0]),
  );
  const peakRefs = new Map(
    bmEntries.map(({ name }) => [name, { peak: baseValue }]),
  );

  const firstOverlayDate = chartData.find(
    (row) => !overlayStartDate || row.date >= overlayStartDate,
  )?.date || chartData[0]?.date;

  bmEntries.forEach(({ name, points }) => {
    if (!firstOverlayDate || !points.length) return;
    const seed = seedBenchmarkPoint(points, firstOverlayDate);
    if (!seed) return;
    const seeded = benchmarkValueForTab(seed, chartTab, baseValue, peakRefs.get(name));
    if (seeded != null) lastValues.set(name, seeded);
  });

  // Keep the account timeline only. Unioning in every benchmark trading day
  // inserts rows with no equity value; Recharts treats those as gaps and the
  // primary curve fragments into disconnected segments.
  const data = chartData.map((chartPoint) => {
    const date = chartPoint.date;
    const row = { ...(chartByDate.get(date) || chartPoint) };

    bmEntries.forEach(({ name }) => {
      const point = pointMaps.get(name)?.get(date);
      if (point) {
        const value = benchmarkValueForTab(point, chartTab, baseValue, peakRefs.get(name));
        if (value != null) lastValues.set(name, value);
      }
      const beforeOverlay = overlayStartDate && date < overlayStartDate;
      if (chartTab === 'equity' && beforeOverlay) {
        row[name] = null;
      } else {
        row[name] = lastValues.get(name);
      }
    });

    return row;
  });

  return {
    data,
    seriesOrder: [...seriesOrder, ...bmEntries.map((entry) => entry.name)],
  };
}
